import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";
import SummaryApi from "../common/index";
import PackageCard from "../components/PackageCard";

const Packages = () => {
  const [packages, setPackages] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePlanName, setActivePlanName] = useState("");
  const [userSubscriptions, setUserSubscriptions] = useState([]);

  // Carousel State
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const isLocked = useRef(false);
  const touchStart = useRef(null);

  const getUserId = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      const parsed = JSON.parse(sessionData);
      return parsed.user_id || parsed.id || parsed;
    } catch (e) {
      return sessionData;
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const currentUserId = getUserId();
      const [planRes, pkgRes] = await Promise.all([
        fetch(SummaryApi.getActivePlan.url, {
          method: SummaryApi.getActivePlan.method,
        }),
        fetch(SummaryApi.getActivePackage.url, {
          method: SummaryApi.getActivePackage.method,
        }),
      ]);

      const planData = await planRes.json();
      const pkgData = await pkgRes.json();

      if (planData.status && pkgData.status) {
        setPlans(planData.data);
        setPackages(pkgData.data);
        if (planData.data.length > 0 && !activePlanName) {
          setActivePlanName(planData.data[0].plan_name);
        }
      }

      if (currentUserId) {
        const subResponse = await fetch(SummaryApi.getSubscriptionByUser.url, {
          method: SummaryApi.getSubscriptionByUser.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: currentUserId }),
        });
        const subData = await subResponse.json();
        if (subData.status) setUserSubscriptions(subData.data);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, [getUserId]);

  const filteredPackages = packages.filter(
    (pkg) => pkg.plan_name === activePlanName,
  );

  // --- CAROUSEL ACTIONS ---
  const handleNext = useCallback(() => {
    if (isLocked.current || filteredPackages.length <= 1) return;
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % filteredPackages.length);
    lockControls();
  }, [filteredPackages.length]);

  const handlePrev = useCallback(() => {
    if (isLocked.current || filteredPackages.length <= 1) return;
    setDirection(-1);
    setCurrentIndex(
      (prev) => (prev - 1 + filteredPackages.length) % filteredPackages.length,
    );
    lockControls();
  }, [filteredPackages.length]);

  const goToIndex = (index) => {
    if (index === currentIndex) return;
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  };

  const lockControls = () => {
    isLocked.current = true;
    setTimeout(() => {
      isLocked.current = false;
    }, 400);
  };

  // Wheel and Touch Logic
  const handleWheel = (e) => {
    // We only trigger if the scroll is horizontal or significant vertical
    if (Math.abs(e.deltaX) > 30) {
      if (e.deltaX > 0) handleNext();
      else handlePrev();
    }
  };

  const onTouchStart = (e) => {
    touchStart.current = e.targetTouches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const touchEnd = e.changedTouches[0].clientX;
    const distance = touchStart.current - touchEnd;
    if (Math.abs(distance) > 50) {
      if (distance > 0) handleNext();
      else handlePrev();
    }
    touchStart.current = null;
  };

  const isSubscriptionBlocked = userSubscriptions.some((sub) => {
    const endDate = new Date(sub.package_end_date);
    return (
      sub.is_active === 1 && (sub.is_expired === 0 || new Date() < endDate)
    );
  });

  const activePlanId = plans.find(
    (p) => p.plan_name === activePlanName,
  )?.plan_id;

  const parseSpecifications = (htmlString) => {
    if (!htmlString) return [];
    const regex = /<li>(.*?)<\/li>/g;
    const items = [];
    let match;
    while ((match = regex.exec(htmlString)) !== null) {
      items.push(match[1]);
    }
    return items;
  };

  return (
    <div className="min-h-screen bg-[#FFF5F7] p-4 lg:p-12 font-sans text-gray-900 overflow-x-hidden">
      <div className="max-w-7xl mx-auto">
        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-pink-100 text-pink-600 px-4 py-1 rounded-full text-xs font-bold mb-4 uppercase">
            <Sparkles size={14} /> Premium Care
          </div>
          <h1 className="text-4xl lg:text-5xl font-extrabold text-pink-800">
            Choose Your Plan
          </h1>
        </div>

        {/* Plan Selectors */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex p-1.5 bg-white shadow-sm border border-pink-100 rounded-2xl">
            {plans.map((plan) => (
              <button
                key={plan.plan_id}
                onClick={() => {
                  setActivePlanName(plan.plan_name);
                  setCurrentIndex(0);
                }}
                className={`px-8 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  activePlanName === plan.plan_name
                    ? "bg-pink-500 text-white shadow-lg"
                    : "text-pink-400 hover:text-pink-600"
                }`}
              >
                {plan.plan_name}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-40">
            <div className="w-12 h-12 border-4 border-pink-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredPackages.length > 0 ? (
          <div
            className="relative"
            onWheel={handleWheel}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            {/* Arrows (Desktop Only) */}
            <button
              onClick={handlePrev}
              className="hidden md:flex absolute -left-6 top-1/2 -translate-y-1/2 z-40 bg-white p-4 rounded-full shadow-lg text-pink-600 hover:bg-pink-500 hover:text-white transition-all"
            >
              <ChevronLeft size={24} strokeWidth={3} />
            </button>
            <button
              onClick={handleNext}
              className="hidden md:flex absolute -right-6 top-1/2 -translate-y-1/2 z-40 bg-white p-4 rounded-full shadow-lg text-pink-600 hover:bg-pink-500 hover:text-white transition-all"
            >
              <ChevronRight size={24} strokeWidth={3} />
            </button>

            {/* Carousel Container */}
            <div className="overflow-hidden py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <AnimatePresence initial={false} mode="popLayout">
                  {[0, 1, 2].map((offset) => {
                    const pkgIndex =
                      (currentIndex + offset) % filteredPackages.length;
                    const pkg = filteredPackages[pkgIndex];
                    const userSub = userSubscriptions.find(
                      (s) => String(s.package_id) === String(pkg.package_id),
                    );

                    return (
                      <motion.div
                        key={`${activePlanName}-${pkgIndex}-${offset}`}
                        initial={{ opacity: 0, x: direction * 100 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -direction * 100 }}
                        transition={{
                          type: "spring",
                          stiffness: 300,
                          damping: 30,
                        }}
                        className={`${offset >= 1 ? "hidden md:block" : ""} ${offset >= 2 ? "hidden lg:block" : ""}`}
                      >
                        <PackageCard
                          plan_id={activePlanId}
                          package_id={pkg.package_id}
                          name={pkg.package_name}
                          actual_price={pkg.actual_price}
                          selling_price={pkg.selling_price}
                          padCount={`${pkg.no_of_leafs} Pads Total`}
                          rate={`₹${(parseFloat(pkg.selling_price) / (pkg.no_of_leafs || 1)).toFixed(1)}/pad`}
                          title={pkg.title}
                          subtitle={pkg.subtitle}
                          onSubscribeSuccess={fetchData}
                          isSubscribed={
                            userSub?.is_active === 1 &&
                            userSub?.is_expired === 0
                          }
                          isUsed={userSub?.is_used === 1}
                          canSubscribe={!isSubscriptionBlocked}
                           features={pkg.specifications}
                        duration={pkg.duration} 
                        />
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            </div>

            {/* CAROUSEL DOTS */}
            <div className="flex justify-center items-center gap-3 mt-10">
              {filteredPackages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goToIndex(i)}
                  className={`transition-all duration-300 rounded-full ${
                    currentIndex === i
                      ? "w-8 h-2.5 bg-pink-500 shadow-md shadow-pink-200"
                      : "w-2.5 h-2.5 bg-pink-200 hover:bg-pink-300"
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-pink-200">
            <h3 className="text-xl font-semibold text-pink-800">
              No packages currently available.
            </h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default Packages;
